import React, { useState, useEffect } from 'react';
import './App.css';
import Home from './components/Home';
import History from './components/History';
import Report from './components/Report';
import Subscription from './components/Subscription';
import Profile from './components/Profile';
import { API_URL } from './config';
import { apiFetch, getAuthHeaders } from './api';

interface ActiveScan {
  id: string;
  siret: string;
  company_name: string;
  progress: number;
  score: number | null;
  loading: boolean;
  checked_at: string;
  risk_level: string;
}

function App() {
  const [currentView, setCurrentView] = useState<'home' | 'history' | 'report' | 'subscription' | 'profile'>('home');
  const [viewingSiret, setViewingSiret] = useState<string | null>(null);
  const [viewingReportId, setViewingReportId] = useState<string | null>(null);
  const [activeScans, setActiveScans] = useState<ActiveScan[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [userPrenom, setUserPrenom] = useState(() => {
    return localStorage.getItem('userPrenom') || '';
  });
  const [userEmail, setUserEmail] = useState(() => {
    return localStorage.getItem('userEmail') || '';
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userMenuTimeout, setUserMenuTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showQuotaTooltip, setShowQuotaTooltip] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<{ left: number; total: number; renewalDate: string | null }>({ left: 0, total: 1, renewalDate: null });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [toast, setToast] = useState<{ message: string; show: boolean }>({ message: '', show: false });
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerData, setRegisterData] = useState({
    nom: '',
    prenom: '',
    email: '',
    societe: '',
    siret: '',
    tva: '',
    password: ''
  });
  const [registerError, setRegisterError] = useState('');
  const [siretLoading, setSiretLoading] = useState(false);

  const handleViewReport = (siretOrReportId: string, isReportId: boolean = false) => {
    if (siretOrReportId === 'history') {
      setCurrentView('history');
      return;
    }
    if (isReportId) {
      setViewingReportId(siretOrReportId);
      setViewingSiret(null);
    } else {
      setViewingSiret(siretOrReportId);
      setViewingReportId(null);
    }
    setCurrentView('report');
  };

  const handleStartScan = (scan: ActiveScan) => {
    setActiveScans(prev => [scan, ...prev]);
  };

  const handleUpdateScan = (id: string, updates: Partial<ActiveScan>) => {
    setActiveScans(prev => prev.map(scan =>
      scan.id === id ? { ...scan, ...updates } : scan
    ));
  };

  const handleRemoveScan = (id: string) => {
    setActiveScans(prev => prev.filter(scan => scan.id !== id));
    // Recharger le quota après chaque vérification
    if (userEmail) {
      loadQuotaInfo(userEmail);
    }
  };

  const showToast = (message: string) => {
    setToast({ message, show: true });
    setTimeout(() => {
      setToast({ message: '', show: false });
    }, 3000);
  };

  useEffect(() => {
    if (isAuthenticated && userEmail) {
      loadQuotaInfo(userEmail);
    }
  }, [isAuthenticated, userEmail]);

  const loadQuotaInfo = async (email: string) => {
    try {
      const response = await apiFetch(`/api/subscription/status/${email}`);
      const data = await response.json();
      if (data.success) {
        setQuotaInfo({
          left: data.subscription.verificationsLeft,
          total: data.subscription.verificationsTotal,
          renewalDate: data.subscription.renewalDate
        });
      }
    } catch (err) {
      console.error('Erreur chargement quota:', err);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
        setUserPrenom(data.user.prenom);
        setUserEmail(data.user.email);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userPrenom', data.user.prenom);
        localStorage.setItem('userEmail', data.user.email);
        localStorage.setItem('authToken', data.token);
        setShowLoginModal(false);
        setLoginEmail('');
        setLoginPassword('');
        loadQuotaInfo(data.user.email);
        showToast('Connexion réussie');
      } else {
        setLoginError(data.message || 'Identifiants incorrects');
      }
    } catch (err) {
      setLoginError('Erreur de connexion');
    }
  };

  const handleLogout = () => {
    showToast('Déconnexion réussie');
    setShowUserMenu(false);
    setTimeout(() => {
      setIsAuthenticated(false);
      setUserPrenom('');
      setUserEmail('');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userPrenom');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('authToken');
      setCurrentView('home');
    }, 1000);
  };

  const handleBackFromReport = () => {
    setViewingSiret(null);
    setViewingReportId(null);
    setCurrentView('history');
  };

  const handleGoHome = () => {
    setCurrentView('home');
    setViewingSiret(null);
  };

  const handleGoHistory = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setCurrentView('history');
  };

  const handleSiretLookup = async (siret: string) => {
    if (siret.length !== 14) return;

    setSiretLoading(true);
    setRegisterError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/company-info/${siret}`);
      const data = await response.json();

      if (data.success) {
        setRegisterData(prev => ({
          ...prev,
          societe: data.data.nom,
          tva: data.data.tva
        }));
      } else {
        setRegisterError('SIRET non trouvé');
      }
    } catch (err) {
      setRegisterError('Erreur lors de la recherche du SIRET');
    } finally {
      setSiretLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData)
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
        setUserPrenom(registerData.prenom);
        setUserEmail(registerData.email);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userPrenom', registerData.prenom);
        localStorage.setItem('userEmail', registerData.email);
        setShowRegisterModal(false);
        loadQuotaInfo(registerData.email);
        setRegisterData({
          nom: '',
          prenom: '',
          email: '',
          societe: '',
          siret: '',
          tva: '',
          password: ''
        });
        showToast('Inscription réussie');
      } else {
        setRegisterError(data.message || 'Erreur lors de l\'inscription');
      }
    } catch (err) {
      setRegisterError('Erreur de connexion');
    }
  };

  return (
    <div className="app">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <button onClick={handleGoHome} className="logo-btn">
              <h1>B2BCheck</h1>
            </button>
            <nav className="header-nav">
              <button
                className={currentView === 'home' ? 'nav-btn active' : 'nav-btn'}
                onClick={handleGoHome}
              >
                Vérification
              </button>
              <button
                className={currentView === 'history' ? 'nav-btn active' : 'nav-btn'}
                onClick={handleGoHistory}
              >
                Rapport
              </button>
              {isAuthenticated && (
                <div className="quota-btn-wrapper"
                  onMouseEnter={() => setShowQuotaTooltip(true)}
                  onMouseLeave={() => setShowQuotaTooltip(false)}
                >
                  <button className="nav-btn quota-btn">
                    {(() => {
                      const bonus = Math.max(0, quotaInfo.left - quotaInfo.total);
                      const standard = Math.min(quotaInfo.left, quotaInfo.total);
                      return (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span className="quota-badge quota-standard">{standard}/{quotaInfo.total}</span>
                          {bonus > 0 && (
                            <span className="quota-badge quota-bonus">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                                <polyline points="20 12 20 22 4 22 4 12"></polyline>
                                <rect x="2" y="7" width="20" height="5"></rect>
                                <line x1="12" y1="22" x2="12" y2="7"></line>
                                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
                                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
                              </svg>
                              {bonus}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </button>
                  {showQuotaTooltip && (
                    <div className="quota-tooltip">
                      {(() => {
                        const bonus = Math.max(0, quotaInfo.left - quotaInfo.total);
                        const standard = Math.min(quotaInfo.left, quotaInfo.total);

                        return (
                          <>
                            <div className="quota-section">
                              <div className="quota-label">Requêtes mensuelles : {standard}/{quotaInfo.total}</div>
                              <div className="quota-progress">
                                <div
                                  className="quota-progress-bar quota-progress-standard"
                                  style={{ width: `${(standard / quotaInfo.total) * 100}%` }}
                                ></div>
                              </div>
                            </div>

                            {bonus > 0 && (
                              <div className="quota-section">
                                <div className="quota-label">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
                                    <polyline points="20 12 20 22 4 22 4 12"></polyline>
                                    <rect x="2" y="7" width="20" height="5"></rect>
                                    <line x1="12" y1="22" x2="12" y2="7"></line>
                                    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
                                    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
                                  </svg>
                                  Crédits bonus : {bonus}
                                </div>
                              </div>
                            )}

                            <div className="quota-total">
                              Total disponible : {quotaInfo.left} requête{quotaInfo.left > 1 ? 's' : ''}
                            </div>

                            {quotaInfo.renewalDate && (
                              <p className="quota-renewal">
                                Réinitialisation le {new Date(quotaInfo.renewalDate).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
              {isAuthenticated ? (
                <div
                  className="user-menu-wrapper"
                  onMouseEnter={() => {
                    if (userMenuTimeout) clearTimeout(userMenuTimeout);
                    setShowUserMenu(true);
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => {
                      setShowUserMenu(false);
                    }, 300);
                    setUserMenuTimeout(timeout);
                  }}
                >
                  <button className="nav-btn user-menu-btn">
                    {userPrenom}
                  </button>
                  {showUserMenu && (
                    <div className="user-dropdown">
                      <button className="dropdown-item" onClick={() => { setCurrentView('profile'); setShowUserMenu(false); }}>Profil</button>
                      <button className="dropdown-item" onClick={() => { setCurrentView('subscription'); setShowUserMenu(false); }}>Abonnement</button>
                      <button className="dropdown-item" onClick={handleLogout}>Déconnexion</button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button className="nav-btn" onClick={() => setShowLoginModal(true)}>
                    Connexion
                  </button>
                  <button className="nav-btn" onClick={() => setShowRegisterModal(true)}>
                    Inscription
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="modal-content login-modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="Email"
                  required
                />
              </div>
              <div className="form-group">
                <label>Mot de passe</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Mot de passe"
                  required
                />
              </div>
              {loginError && <div className="login-error">{loginError}</div>}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowLoginModal(false)} className="btn-cancel">
                  Annuler
                </button>
                <button type="submit" className="btn-confirm">
                  Connexion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRegisterModal && (
        <div className="modal-overlay" onClick={() => setShowRegisterModal(false)}>
          <div className="modal-content login-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Inscription</h2>
            <form onSubmit={handleRegister}>
              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  value={registerData.nom}
                  onChange={(e) => setRegisterData({...registerData, nom: e.target.value})}
                  placeholder="Nom"
                  required
                />
              </div>
              <div className="form-group">
                <label>Prénom</label>
                <input
                  type="text"
                  value={registerData.prenom}
                  onChange={(e) => setRegisterData({...registerData, prenom: e.target.value})}
                  placeholder="Prénom"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  placeholder="Email"
                  required
                />
              </div>
              <div className="form-group">
                <label>SIRET (facultatif)</label>
                <input
                  type="text"
                  value={registerData.siret}
                  onChange={(e) => {
                    const value = e.target.value;
                    setRegisterData({...registerData, siret: value});
                    if (value.length === 14) {
                      handleSiretLookup(value);
                    } else if (value.length === 0) {
                      setRegisterData({...registerData, siret: '', societe: '', tva: ''});
                    }
                  }}
                  placeholder="SIRET (14 chiffres)"
                  maxLength={14}
                />
                {siretLoading && <span style={{fontSize: '0.9em', color: '#666'}}>Recherche en cours...</span>}
              </div>
              {registerData.societe && (
                <div className="form-group">
                  <label>Société</label>
                  <input
                    type="text"
                    value={registerData.societe}
                    onChange={(e) => setRegisterData({...registerData, societe: e.target.value})}
                    placeholder="Société"
                    readOnly
                  />
                </div>
              )}
              {registerData.tva && (
                <div className="form-group">
                  <label>TVA</label>
                  <input
                    type="text"
                    value={registerData.tva}
                    onChange={(e) => setRegisterData({...registerData, tva: e.target.value})}
                    placeholder="Numéro de TVA"
                    readOnly
                  />
                </div>
              )}
              <div className="form-group">
                <label>Mot de passe</label>
                <input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                  placeholder="Mot de passe"
                  required
                />
              </div>
              {registerError && <div className="login-error">{registerError}</div>}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowRegisterModal(false)} className="btn-cancel">
                  Annuler
                </button>
                <button type="submit" className="btn-confirm">
                  S'inscrire
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="app-content">
        {currentView === 'home' && (
          <Home
            onViewReport={handleViewReport}
            onStartScan={handleStartScan}
            onUpdateScan={handleUpdateScan}
            onRemoveScan={handleRemoveScan}
            isAuthenticated={isAuthenticated}
            onRequestAuth={() => setShowLoginModal(true)}
            userEmail={userEmail}
            quotaLeft={quotaInfo.left}
          />
        )}
        {currentView === 'history' && (
          <History
            onViewReport={handleViewReport}
            activeScans={activeScans}
            onStartScan={handleStartScan}
            onUpdateScan={handleUpdateScan}
            onRemoveScan={handleRemoveScan}
            onGoToSearch={(query) => {
              setCurrentView('home');
              setTimeout(() => {
                const event = new CustomEvent('triggerSearch', { detail: { query } });
                window.dispatchEvent(event);
              }, 10);
            }}
            userEmail={userEmail}
            quotaLeft={quotaInfo.left}
          />
        )}
        {currentView === 'report' && (viewingSiret || viewingReportId) && (
          <Report siret={viewingSiret} reportId={viewingReportId} onBack={handleBackFromReport} />
        )}
        {currentView === 'subscription' && (
          <Subscription onBack={() => setCurrentView('home')} userEmail={userEmail} />
        )}
        {currentView === 'profile' && (
          <Profile userEmail={userEmail} onProfileUpdate={(prenom) => {
            setUserPrenom(prenom);
            localStorage.setItem('userPrenom', prenom);
          }} />
        )}
      </div>
      {toast.show && (
        <div className="toast">
          {toast.message}
        </div>
      )}

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-links">
            <a href="/mentions-legales" className="footer-link">Mentions légales</a>
            <a href="/cgu" className="footer-link">CGU</a>
            <a href="/cgv" className="footer-link">CGV</a>
            <a href="/politique-confidentialite" className="footer-link">Politique de confidentialité</a>
            <a href="/cookies" className="footer-link">Cookies</a>
            <a href="mailto:contact@b2bcheck.fr" className="footer-link">Contact</a>
          </div>
          <div className="footer-copyright">
            © {new Date().getFullYear()} B2BCheck - Tous droits réservés
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
